// LISTA-EXPORT — a legacy CRM „Export" gombjának utódja (XLSX · CSV · JSON), a 6 érintett lista-oldalra
// (ügyfelek, fiókok, kapcsolattartók, munkatársak, eszközök, eseménynapló).
//
// ── HÁROM DÖNTÉS, AMIÉRT NEM A LEGACY-T PORTOLTUK ────────────────────────────────────────────────────────
//
// 1. **A LEGACY A DOM-OT KAPARTA.** Az export-gomb egy `getExportData(tableId)` JS-függvényt hívott, ami a
//    KIRENDERELT `<table>`-ből olvasta a fejlécet és a sorokat (`app.js`), majd azt küldte fel a szervernek.
//    Következmény: az „export" mindig csak az ÉPPEN LÁTSZÓ LAP (max 50 sor) volt — aki 300 ügyfelet akart
//    kimenteni, 50-et kapott, hibajelzés nélkül. Ráadásul a fejléc-tömbből kiestek a `no-export` oszlopok, a
//    sor-tömbből viszont NEM (üres stringgé váltak) → a két tömb hossza eltért, az oszlopok elcsúsztak.
//    ITT: a sorok a LISTA-VÉGPONTRÓL jönnek, ugyanazokkal a szűrőkkel, ADDIG lapozva, amíg van adat.
//
// 2. **A LEGACY JSON-JA MÁST EXPORTÁLT, MINT AZ XLSX/CSV.** A `json` ág eldobta a kapott (szűrt) adatot, és
//    újra lekérdezte a TELJES táblát (`UserService::exportContacts`) — más tartalom, más mező-készlet,
//    ugyanaz a gomb. ITT: mind a három formátum UGYANAZT a sor- és oszlop-készletet írja.
//
// 3. **AZ OSZLOPOK MOST EXPLICITEK.** Nincs szöveg-kinyerés JSX-ből: egy oszlop akkor és csak akkor kerül az
//    exportba, ha van `exportValue`-ja (`DataTableColumn`). Így egy új oszlop NEM kerül be véletlenül
//    félig-renderelt szöveggel, és a művelet-oszlop sem igényel `no-export` jelölést.
import { toCsv } from './csv';

import type { DataTableColumn } from '@/components/shared/data-table';

export type ExportFormat = 'xlsx' | 'csv' | 'json';

/** Az export-cella típusa — a `.xlsx` valódi szám/dátum cellát kap, a CSV/JSON a kanonikus alakot. */
export type ExportCellType = 'text' | 'number' | 'money' | 'date' | 'datetime';

/** Egy export-oszlop: a fejléc-felirat, a sor→érték leképezés és a cella TÍPUSA. */
export interface ExportColumn<T> {
  header: string;
  value: (row: T) => string | number | boolean | null | undefined;
  /** Default `text`. A tipizált oszlop `value`-ja NYERS értéket ad (szám/Decimal-string vagy ISO-dátum). */
  type?: ExportCellType;
}

/**
 * A tábla-oszlopokból az EXPORT-oszlopok: csak azok, amelyeknek van `exportValue`-ja, a tábla SORRENDJÉBEN.
 * A fejléc a `header` (string) — ha egy oszlop fejléce nem szöveg (pl. csak ikon), az `id` a fallback.
 */
export function exportColumnsOf<T>(columns: DataTableColumn<T>[]): ExportColumn<T>[] {
  return columns
    .filter(
      (
        c,
      ): c is DataTableColumn<T> & {
        exportValue: NonNullable<DataTableColumn<T>['exportValue']>;
      } => typeof c.exportValue === 'function',
    )
    .map((c) => ({
      header: typeof c.header === 'string' && c.header !== '' ? c.header : c.id,
      value: c.exportValue,
      ...(c.exportType ? { type: c.exportType } : {}),
    }));
}

/**
 * Fájlnév: `<slug>_YYYY-MM-DD.<ext>`. A slug ANGOL és a nyelvtől független (a fájlnév gépi felület is —
 * szkriptek, mappák, e-mail-csatolmány), a dátum ISO-alak, hogy név szerint rendezve időrendben álljon.
 * A legacy `uniqid()`-jét NEM visszük: ugyanaznapi két export ütközését a böngésző `(1)` utótagja rendezi.
 */
export function exportFileName(slug: string, format: ExportFormat, now: Date): string {
  const iso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return `${slug}_${iso}.${format}`;
}

/**
 * Egy TIPIZÁLT cella nyers értékének értelmezése. `null` → nincs érték (üres cella). A szám a Decimal-STRINGET
 * is elfogadja (a pénz a dráton string — sosem float), a dátum az ISO-instantot és a dátum-onlyt is.
 *
 * MIÉRT SZÁMÍT: a `.xlsx`-ben a szám/dátum cella VALÓDI típust kap, tehát összegezhető és rendezhető; a
 * CSV/JSON pedig a kanonikus alakot viszi (nyers szám, ISO-dátum), amit egy másik rendszer be tud olvasni.
 * Ha az érték nem értelmezhető (pl. „—" vagy szöveges jelölés), a cella SZÖVEG marad — sosem találunk ki
 * számot vagy dátumot.
 */
function numericOf(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (t === '' || !/^-?\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function dateOf(v: unknown): Date | null {
  if (typeof v !== 'string' || v.trim() === '') return null;
  const ms = Date.parse(v);
  return Number.isNaN(ms) ? null : new Date(ms);
}

/**
 * A SZÖVEGES formátumok (CSV/JSON) cella-értéke: a tipizált oszlop KANONIKUS alakot ad (nyers szám ponttal,
 * dátum ISO-ban), a `text` oszlop változatlanul a `value`-t. Így a CSV gépi feldolgozásra alkalmas — ez az
 * iparági elvárás (a Stripe/Shopify export is kanonikus értéket ad, nem a felületi formázást).
 */
function canonical<T>(col: ExportColumn<T>, row: T): string | number | boolean | null {
  const raw = col.value(row) ?? null;
  if (raw === null || col.type === undefined || col.type === 'text') return raw;
  if (col.type === 'number' || col.type === 'money') return numericOf(raw) ?? raw;
  const d = dateOf(raw);
  if (!d) return raw;
  // `date` → naptári nap (a zóna-eltolás nem csúsztathatja el); `datetime` → teljes ISO-instant.
  return col.type === 'date' ? d.toISOString().slice(0, 10) : d.toISOString();
}

/** Sor-értékek mátrixa (fejléc nélkül) — a CSV/XLSX író bemenete. */
function matrix<T>(columns: ExportColumn<T>[], rows: T[]): (string | number | boolean | null)[][] {
  return rows.map((r) => columns.map((c) => canonical(c, r)));
}

/**
 * A böngésző letöltés-indítása egy Blobból (object-URL azonnal felszabadítva — nincs memória-szivárgás).
 * EXPORTÁLT, mert a kiadás-import SABLONJA ugyanezt az utat használja — két letöltés-út elcsúszna.
 */
export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * A JSON-alak OBJEKTUM-tömb (nem mátrix): a kulcs a fejléc-felirat, az érték a cella. Így a JSON önmagát
 * dokumentálja, és a legacy `JSON_PRETTY_PRINT`-jével egyező olvashatóságot ad (2 szóköz behúzás).
 */
function toJson<T>(columns: ExportColumn<T>[], rows: T[]): string {
  return JSON.stringify(
    rows.map((r) => Object.fromEntries(columns.map((c) => [c.header, canonical(c, r)]))),
    null,
    2,
  );
}

/**
 * A SZÖVEGES formátumok (CSV/JSON) TISZTA szerializálója — szándékosan kiemelve a letöltésből: így a
 * FÁJL-TARTALOM közvetlenül tesztelhető, `Blob`/DOM nélkül (a jsdom `Blob`-jának nincs is `text()`-je).
 * Az `exportRows` ezt használja, tehát a teszt a VALÓDI kimenetet nézi, nem egy párhuzamos implementációt.
 */
export function serializeRows<T>(
  format: 'csv' | 'json',
  columns: ExportColumn<T>[],
  rows: T[],
): { text: string; mime: string } {
  if (format === 'csv') {
    return {
      text: toCsv(
        columns.map((c) => c.header),
        matrix(columns, rows),
      ),
      // A `charset=utf-8`: a letöltés-kezelő és a szövegszerkesztő is helyesen nyitja (a BOM mellé).
      mime: 'text/csv;charset=utf-8',
    };
  }
  return { text: toJson(columns, rows), mime: 'application/json;charset=utf-8' };
}

/**
 * XLSX-írás. A `write-excel-file` browser-buildje **lazy import**-tal jön: a csomag csak akkor kerül a
 * hálózatra, ha a felhasználó tényleg Excelt kér (a lista-oldalak kezdő-bundle-je nem hízik tőle).
 * A formázás a legacy-vel egyező: **félkövér fejléc** + oszlop-szélességek (a legacy „autoSize"-ának a
 * kiszámított párja — az xlsx-formátumban nincs valódi auto-size, a szélességet a tartalomból becsüljük).
 */
async function writeXlsx<T>(
  columns: ExportColumn<T>[],
  rows: T[],
  filename: string,
): Promise<void> {
  // A csomagnak NINCS gyökér-exportja, csak alútvonalak (`/browser`, `/node`, …) — a böngésző-build kell:
  // az adja a `toBlob()`-ot, és a nehéz XML-generálást Web Workerben futtatja (nem blokkolja a UI-t).
  const { default: writeExcelFile } = await import('write-excel-file/browser');

  // ⚠️ A KÉT OVERLOAD KÖZÜL AZ „OBJEKTUM"-OS KELL. A `writeExcelFile` első paramétere lehet 2D CELLA-MÁTRIX
  // vagy OBJEKTUM-TÖMB — és a `columns` opció MÁST JELENT a kettőben: a mátrix-változatban csak szélesség/
  // formátum (a `header` mezőt ELDOBJA), az objektum-változatban viszont ez adja a FEJLÉC-SORT és a
  // cella-leképezést. Mátrixot átadva a fájl fejléc NÉLKÜL, sima szedéssel készült el (mérve: 475 sor a 476
  // helyett, félkövér nélkül) — némán, hibajelzés nélkül. Ezért itt a NYERS sorokat adjuk át objektumként.
  const data = matrix(columns, rows);
  const schema = columns.map((c, i) => ({
    // A fejléc egy CELLA-objektum (nem sima string): így kap félkövér szedést — a legacy XLSX-formázásával
    // egyezően (`$sheet->getStyle($headerRange)->getFont()->setBold(TRUE)`).
    header: { value: c.header, fontWeight: 'bold' as const },
    // ÜRES cella: a `value` KULCSOT hagyjuk ki (nem `null`/`undefined` értéket adunk) — a lib `Value` típusa
    // nem ismeri a null-t, és `exactOptionalPropertyTypes` alatt a `value: undefined` sem érvényes. A repo
    // bevett mintája a feltételes spread.
    cell: (row: T) => {
      const v = c.value(row);
      if (v === null || v === undefined || v === '') return {};
      // TIPIZÁLT CELLA: a szám/dátum VALÓDI Excel-típussal megy ki (formátummal), tehát összegezhető,
      // rendezhető, szűrhető. Ha az érték nem értelmezhető számként/dátumként, SZÖVEGKÉNT marad — sosem
      // találunk ki értéket (egy „—" vagy „n. a." cellából nem lesz 0, se 1970-01-01).
      if (c.type === 'number' || c.type === 'money') {
        const n = numericOf(v);
        return n === null ? { value: String(v) } : { value: n };
      }
      if (c.type === 'date' || c.type === 'datetime') {
        const d = dateOf(v);
        return d === null ? { value: String(v) } : { value: d };
      }
      return { value: v };
    },
    // A TÍPUS és a FORMÁTUM a séma-oszlopon: a `write-excel-file` ebből tudja, milyen cellát írjon.
    // A pénz két tizedessel és ezres-csoportosítással, a dátum ISO-alakban (nyelv-független, rendezhető).
    ...(c.type === 'number' ? { type: Number } : {}),
    ...(c.type === 'money' ? { type: Number, format: '#,##0.00' } : {}),
    ...(c.type === 'date' ? { type: Date, format: 'yyyy-mm-dd' } : {}),
    ...(c.type === 'datetime' ? { type: Date, format: 'yyyy-mm-dd hh:mm' } : {}),
    // Szélesség karakterben: a leghosszabb tartalom (fejléc is), 10 és 60 közé szorítva — a 60 fölötti
    // cellák (jegyzet, cím) törnek, nem nyújtják el a lapot vízszintesen kezelhetetlenné. Ez a legacy
    // „autoSize" kiszámított párja (az xlsx-formátumban nincs valódi auto-size).
    width: Math.min(
      60,
      Math.max(10, ...data.map((r) => String(r[i] ?? '').length), c.header.length + 2),
    ),
  }));
  const blob = await writeExcelFile(rows, { columns: schema, sheet: 'Export' }).toBlob();
  download(blob, filename);
}

export interface ExportRowsInput<T> {
  format: ExportFormat;
  /** Az export-oszlopok (a `DataTableColumn[]`-ból az `exportColumnsOf`-fal). */
  columns: ExportColumn<T>[];
  rows: T[];
  /** Fájlnév-slug (angol, nyelv-független) — pl. `contacts`. */
  slug: string;
  /** A fájlnév dátum-része (injektálva → tesztelhető). */
  now?: Date;
}

/** A teljes export egy hívásban: formátum-specifikus szerializálás + letöltés-indítás. */
export async function exportRows<T>({
  format,
  columns,
  rows,
  slug,
  now = new Date(),
}: ExportRowsInput<T>): Promise<void> {
  const filename = exportFileName(slug, format, now);
  if (format === 'xlsx') {
    await writeXlsx(columns, rows, filename);
    return;
  }
  const { text, mime } = serializeRows(format, columns, rows);
  download(new Blob([text], { type: mime }), filename);
}

/**
 * A TELJES (szűrt) sor-készlet összegyűjtése lapozással — ez váltja ki a legacy „csak az aktuális lap"
 * viselkedését. A `fetchPage` a lista-végpont egy lapját adja; a ciklus addig fut, amíg van `next`.
 *
 * A `maxRows` plafon NEM csendes csonkolás: a hívó a visszatérő `truncated` jelzőből TUD róla, és
 * figyelmeztetést mutat. (Enélkül egy 50 000 soros eseménynapló-export a böngészőt vinné el.)
 */
export async function collectPages<T, C>(
  fetchPage: (cursor: C | undefined) => Promise<{ rows: T[]; next: C | null } | null>,
  maxRows = 10_000,
  maxPages = 200,
): Promise<{ rows: T[]; truncated: boolean }> {
  const rows: T[] = [];
  let cursor: C | undefined;
  for (let i = 0; i < maxPages; i++) {
    const page = await fetchPage(cursor);
    // `null` → a lap nem tölthető (jog/hálózat): a hívó a MÁR összegyűjtött sorokat kapja, `truncated`-del.
    if (!page) return { rows, truncated: true };
    rows.push(...page.rows);
    if (rows.length >= maxRows) return { rows: rows.slice(0, maxRows), truncated: true };
    if (page.next === null) return { rows, truncated: false };
    cursor = page.next;
  }
  return { rows, truncated: true };
}
